import { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Clock, Check } from 'lucide-react';

const ITEM_H = 36;      // px per item (h-9)
const VISIBLE = 5;      // visible rows
const PAD = ((VISIBLE - 1) / 2) * ITEM_H; // top/bottom padding so ends can center

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));

/** A single scroll-snap wheel column (touch-scrollable on iOS). */
const Wheel = ({ items, value, onChange, testId }) => {
  const ref = useRef(null);
  const settle = useRef(null);

  useEffect(() => {
    const idx = Math.max(0, items.indexOf(value));
    if (ref.current) ref.current.scrollTop = idx * ITEM_H;
  }, []); // position on mount

  const handleScroll = () => {
    clearTimeout(settle.current);
    settle.current = setTimeout(() => {
      if (!ref.current) return;
      const idx = Math.max(0, Math.min(items.length - 1, Math.round(ref.current.scrollTop / ITEM_H)));
      ref.current.scrollTo({ top: idx * ITEM_H, behavior: 'smooth' });
      if (items[idx] !== value) onChange(items[idx]);
    }, 120);
  };

  return (
    <div className="relative" style={{ height: VISIBLE * ITEM_H, width: 64 }}>
      <div
        ref={ref}
        onScroll={handleScroll}
        data-testid={testId}
        className="h-full overflow-y-scroll [&::-webkit-scrollbar]:hidden"
        style={{
          scrollSnapType: 'y mandatory',
          scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y',
          overscrollBehavior: 'contain',
        }}
      >
        <div style={{ height: PAD }} />
        {items.map((it) => (
          <div
            key={it}
            onClick={() => { ref.current?.scrollTo({ top: items.indexOf(it) * ITEM_H, behavior: 'smooth' }); onChange(it); }}
            className={`flex items-center justify-center font-mono cursor-pointer transition-colors ${it === value ? 'text-[var(--m-text)] text-lg font-semibold' : 'text-[var(--m-text-muted)] text-base'}`}
            style={{ height: ITEM_H, scrollSnapAlign: 'center' }}
          >
            {it}
          </div>
        ))}
        <div style={{ height: PAD }} />
      </div>
    </div>
  );
};

/**
 * iOS-style scrolling-wheel time picker rendered INLINE (no portal) so touch
 * scrolling works inside scroll-locked dialogs.
 * @param {string} value - "HH:MM" (24h), empty allowed
 * @param {function} onChange - (value: "HH:MM") => void
 * @param {number} minuteStep - default 5
 */
export const WheelTimePicker = ({ value = '', onChange, minuteStep = 5, className = '', testId }) => {
  const [open, setOpen] = useState(false);
  const minutes = Array.from({ length: Math.ceil(60 / minuteStep) }, (_, i) => String(i * minuteStep).padStart(2, '0'));

  const match = /^(\d{1,2}):(\d{1,2})$/.exec(value || '');
  const hh = match ? match[1].padStart(2, '0') : '';
  const mm = match ? match[2].padStart(2, '0') : '';

  const curH = hh || '07';
  const curM = mm || '00';
  const display = hh && mm ? `${hh}:${mm}` : '--:--';

  return (
    <div className="relative">
      <Button
        type="button"
        variant="outline"
        className={`justify-start text-left font-mono ${!value ? 'text-[var(--m-text-secondary)]' : ''} ${className}`}
        onClick={() => setOpen((o) => !o)}
        data-testid={testId}
      >
        <Clock className="mr-1.5 h-3.5 w-3.5 shrink-0" />
        {display}
      </Button>

      {open && (
        <div className="mt-2 rounded-md bg-[var(--m-bg)] border border-[var(--m-border)] p-3" data-testid={testId ? `${testId}-panel` : undefined}>
          <div className="relative flex items-center justify-center gap-1">
            {/* center selection band */}
            <div className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 z-0 rounded-md border-y border-[#ffd108]/50 bg-[#ffd108]/5" style={{ height: ITEM_H }} />
            <div className="relative z-10">
              <Wheel items={HOURS} value={curH} onChange={(h) => onChange(`${h}:${curM}`)} testId={testId ? `${testId}-hours` : undefined} />
            </div>
            <span className="relative z-10 text-[var(--m-text)] text-lg font-mono">:</span>
            <div className="relative z-10">
              <Wheel items={minutes} value={curM} onChange={(m) => onChange(`${curH}:${m}`)} testId={testId ? `${testId}-minutes` : undefined} />
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            className="w-full mt-2 bg-[#ffd108] text-[#1a1a1a] hover:bg-[#ffd108]/90"
            onClick={() => { if (!value) onChange(`${curH}:${curM}`); setOpen(false); }}
            data-testid={testId ? `${testId}-done` : undefined}
          >
            <Check className="h-4 w-4 mr-1" /> Done
          </Button>
        </div>
      )}
    </div>
  );
};

export default WheelTimePicker;
